import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Send, MessageCircle, ShieldAlert, Loader2, ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";

const API = "/api";

/**
 * Reusable chat dialog used by both students and tutors.
 * For security, free-text messaging is disabled — only pre-approved messages
 * from the server's PRESET_MESSAGES list are allowed.
 * Props:
 *   - open, onOpenChange
 *   - role: "student" | "tutor"
 *   - getAuthHeader: () => headers
 */
export default function MessagingDialog({ open, onOpenChange, role, getAuthHeader }) {
  const [view, setView] = useState("list"); // list | new | chat
  const [conversations, setConversations] = useState([]);
  const [availableTutors, setAvailableTutors] = useState([]);
  const [todayStudents, setTodayStudents] = useState([]);
  const [todayDate, setTodayDate] = useState("");
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const headers = { headers: getAuthHeader() };

  // Load preset list once on dialog open
  useEffect(() => {
    if (!open) return;
    axios
      .get(`${API}/messages/presets`)
      .then((r) => setPresets(r.data.messages || []))
      .catch(() => setPresets([]));
  }, [open]);

  const refreshTodayStudents = async () => {
    if (role !== "tutor") return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/messages/tutor/today-students`, headers);
      setTodayStudents(r.data.students || []);
      setTodayDate(r.data.today || "");
    } catch {
      setTodayStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const refreshConvs = async () => {
    setLoading(true);
    try {
      const url =
        role === "student"
          ? `${API}/messages/student/conversations`
          : `${API}/messages/tutor/conversations`;
      const r = await axios.get(url, headers);
      setConversations(r.data.items || []);
    } catch {
      setConversations([]);
    } finally {
      setLoading(false);
    }
  };

  const refreshAvailableTutors = async () => {
    if (role !== "student") return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/messages/student/available-tutors`, headers);
      setAvailableTutors(r.data.tutors || []);
    } catch {
      setAvailableTutors([]);
    } finally {
      setLoading(false);
    }
  };

  const loadConversation = async (conv) => {
    setActiveConv(conv);
    setView("chat");
    setMessages([]);
    setLoading(true);
    try {
      const url =
        role === "student"
          ? `${API}/messages/student/conversation/${conv.id}`
          : `${API}/messages/tutor/conversation/${conv.id}`;
      const r = await axios.get(url, headers);
      setMessages(r.data.messages || []);
    } catch {
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const startConversation = async (tutor) => {
    try {
      const r = await axios.post(
        `${API}/messages/student/start-conversation`,
        { tutor_id: tutor.id },
        headers
      );
      await loadConversation(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start conversation");
    }
  };

  // Tutor opens (or creates) a conversation with one of today's students
  const openTodayStudent = async (student) => {
    try {
      const r = await axios.post(
        `${API}/messages/tutor/start-conversation`,
        { student_id: student.id },
        headers
      );
      await loadConversation(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to open conversation");
    }
  };

  // Initial load when dialog opens
  useEffect(() => {
    if (open) {
      setView(role === "tutor" ? "today" : "list");
      setActiveConv(null);
      setMessages([]);
      setSelectedPreset("");
      if (role === "tutor") {
        refreshTodayStudents();
      } else {
        refreshConvs();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, role]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async (presetText) => {
    const content = (presetText || selectedPreset || "").trim();
    if (!content || !activeConv) return;
    setSending(true);
    try {
      const recipientId =
        role === "student" ? activeConv.tutor_id : activeConv.student_id;
      const url =
        role === "student"
          ? `${API}/messages/student/send`
          : `${API}/messages/tutor/send`;
      const r = await axios.post(
        url,
        { recipient_id: recipientId, content },
        headers
      );
      setMessages((prev) => [...prev, r.data.message]);
      setSelectedPreset("");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.blocked) {
        toast.error(detail.message, { duration: 6000 });
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to send");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl h-[640px] flex flex-col p-0 gap-0 overflow-hidden"
        data-testid="messaging-dialog"
      >
        <DialogHeader className="px-5 py-3 border-b border-slate-200 bg-gradient-to-r from-blue-700 to-blue-900 text-white">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-white flex items-center gap-2">
              {view === "chat" && (
                <button
                  onClick={() => setView(role === "tutor" ? "today" : "list")}
                  className="p-1 hover:bg-white/10 rounded transition-colors"
                  data-testid="back-to-conv-list"
                >
                  <ArrowLeft className="w-4 h-4" />
                </button>
              )}
              {view === "new" && (
                <button
                  onClick={() => setView("list")}
                  className="p-1 hover:bg-white/10 rounded transition-colors"
                  data-testid="back-to-conv-list"
                >
                  <ArrowLeft className="w-4 h-4" />
                </button>
              )}
              <MessageCircle className="w-5 h-5" />
              {view === "list" && "Messages"}
              {view === "today" && (
                <span className="flex flex-col leading-tight">
                  <span>Today's Students</span>
                  {todayDate && (
                    <span className="text-[10px] font-normal opacity-80">
                      {todayDate} (Istanbul)
                    </span>
                  )}
                </span>
              )}
              {view === "new" && "Start a conversation"}
              {view === "chat" &&
                (role === "student"
                  ? activeConv?.tutor_name
                  : activeConv?.student_name)}
            </DialogTitle>
            {view === "list" && role === "student" && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setView("new");
                  refreshAvailableTutors();
                }}
                data-testid="new-conversation-btn"
              >
                New
              </Button>
            )}
            {view === "today" && role === "tutor" && (
              <Button
                size="sm"
                variant="secondary"
                onClick={refreshTodayStudents}
                data-testid="refresh-today-btn"
              >
                Refresh
              </Button>
            )}
          </div>
        </DialogHeader>

        {/* SAFETY BANNER (top) */}
        <div className="px-4 py-2 bg-blue-50 border-b border-blue-200 text-xs text-blue-900 flex items-start gap-2">
          <ShieldAlert className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
          <span>
            For your safety, only <strong>pre-approved quick messages</strong> can be sent.
            Free-text is disabled to prevent sharing of phone numbers, emails, or external platforms.
          </span>
        </div>

        {/* CONTENT */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {loading && (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          )}

          {!loading && view === "today" && role === "tutor" && (
            <div className="flex-1 overflow-y-auto">
              <div className="px-5 py-2 bg-amber-50 border-b border-amber-200 text-[11px] text-amber-900">
                You can only message students who have a class with you <strong>today</strong>.
                The list resets at midnight (Istanbul time).
              </div>
              {todayStudents.length === 0 ? (
                <div className="text-center py-12 px-6 text-sm text-slate-500" data-testid="no-today-students">
                  No students booked with you today.
                </div>
              ) : (
                <>
                  <div className="px-5 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold border-b border-slate-100 bg-slate-50">
                    Select a student to message
                  </div>
                  {todayStudents.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => openTodayStudent(s)}
                      className="w-full text-left px-5 py-3 border-b border-slate-100 hover:bg-blue-50 transition-colors flex items-center justify-between"
                      data-testid={`today-student-${s.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-semibold flex-shrink-0">
                          {s.name?.charAt(0)?.toUpperCase() || "?"}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-slate-900 truncate">
                            {s.name}
                          </p>
                          <p className="text-xs text-slate-500 truncate">
                            {s.time ? `Class at ${s.time}` : "Today"}
                            {s.last_message_preview && (
                              <> · {s.last_message_preview}</>
                            )}
                          </p>
                        </div>
                      </div>
                      {s.unread_tutor > 0 && (
                        <span
                          className="ml-2 bg-blue-600 text-white text-[10px] rounded-full px-1.5 min-w-[18px] text-center"
                          data-testid={`today-unread-${s.id}`}
                        >
                          {s.unread_tutor}
                        </span>
                      )}
                    </button>
                  ))}
                </>
              )}
            </div>
          )}

          {!loading && view === "list" && (
            <div className="flex-1 overflow-y-auto">
              {conversations.length === 0 ? (
                <div className="text-center py-12 px-6 text-sm text-slate-500">
                  No conversations yet.{" "}
                  {role === "student" && "Click 'New' to message a tutor."}
                </div>
              ) : (
                conversations.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => loadConversation(c)}
                    className="w-full text-left px-5 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors flex items-center justify-between"
                    data-testid={`conv-${c.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900 truncate">
                        {role === "student" ? c.tutor_name : c.student_name}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {c.last_message_preview || "No messages yet"}
                      </p>
                    </div>
                    {((role === "student" && c.unread_student > 0) ||
                      (role !== "student" && c.unread_tutor > 0)) && (
                      <span
                        className="ml-2 bg-blue-600 text-white text-[10px] rounded-full px-1.5 min-w-[18px] text-center"
                        data-testid={`unread-${c.id}`}
                      >
                        {role === "student" ? c.unread_student : c.unread_tutor}
                      </span>
                    )}
                  </button>
                ))
              )}
            </div>
          )}

          {!loading && view === "new" && (
            <div className="flex-1 overflow-y-auto">
              {availableTutors.length === 0 ? (
                <div className="text-center py-12 px-6 text-sm text-slate-500">
                  You don't have any classes scheduled with a tutor yet.
                  Book your first class to start a conversation.
                </div>
              ) : (
                availableTutors.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => startConversation(t)}
                    className="w-full text-left px-5 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors flex items-center gap-3"
                    data-testid={`start-conv-tutor-${t.id}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-slate-100 overflow-hidden flex-shrink-0">
                      {t.photo_url ? (
                        <img
                          src={t.photo_url}
                          alt={t.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-500 text-sm">
                          {t.name?.charAt(0)}
                        </div>
                      )}
                    </div>
                    <p className="font-semibold text-slate-900">{t.name}</p>
                  </button>
                ))
              )}
            </div>
          )}

          {!loading && view === "chat" && (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div
                className="flex-1 overflow-y-auto p-4 space-y-3"
                data-testid="message-thread"
              >
                {messages.length === 0 ? (
                  <p className="text-center text-sm text-slate-400 py-6">
                    Send the first message to get started!
                  </p>
                ) : (
                  messages.map((m) => {
                    const isMine =
                      (role === "student" && m.sender_role === "student") ||
                      (role !== "student" && m.sender_role !== "student");
                    return (
                      <div
                        key={m.id}
                        className={cn(
                          "flex",
                          isMine ? "justify-end" : "justify-start"
                        )}
                      >
                        <div
                          className={cn(
                            "max-w-[75%] rounded-2xl px-4 py-2 text-sm",
                            isMine
                              ? "bg-blue-600 text-white rounded-br-sm"
                              : "bg-slate-100 text-slate-900 rounded-bl-sm"
                          )}
                          data-testid={`msg-${m.id}`}
                        >
                          <p className="whitespace-pre-wrap break-words">
                            {m.content}
                          </p>
                          <p
                            className={cn(
                              "text-[10px] mt-1 opacity-70",
                              isMine ? "text-white" : "text-slate-500"
                            )}
                          >
                            {new Date(m.created_at).toLocaleString("en-US", {
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* PRESET MESSAGE PICKER (free text disabled for safety) */}
              <div className="border-t border-slate-200 bg-slate-50 p-3 space-y-2">
                <div className="flex items-center gap-2 text-[11px] text-slate-600 uppercase tracking-wider font-semibold">
                  <ShieldAlert className="w-3 h-3 text-blue-600" />
                  Pre-approved quick messages only
                </div>
                <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                  <select
                    value={selectedPreset}
                    onChange={(e) => setSelectedPreset(e.target.value)}
                    className="flex-1 min-w-0 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-white"
                    data-testid="preset-select"
                  >
                    <option value="">Choose a quick message…</option>
                    {presets.map((m, idx) => {
                      const en = typeof m === "string" ? m : m.en;
                      const tr = typeof m === "string" ? "" : m.tr;
                      return (
                        <option key={idx} value={en}>
                          {en}{tr ? ` — ${tr}` : ""}
                        </option>
                      );
                    })}
                  </select>
                  <Button
                    onClick={() => handleSend()}
                    disabled={sending || !selectedPreset}
                    className="bg-blue-700 hover:bg-blue-800 text-white disabled:opacity-50 disabled:cursor-not-allowed font-semibold px-5 flex-shrink-0 shadow-sm"
                    data-testid="send-message-btn"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Sending…
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send
                      </>
                    )}
                  </Button>
                </div>
                {selectedPreset && !sending && (
                  <p className="text-[11px] text-blue-700 pt-1" data-testid="send-hint">
                    Ready to send — click the <strong>Send</strong> button →
                  </p>
                )}
                {/* Quick chips (the 4 most-used presets) */}
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {presets.slice(0, 4).map((m, idx) => {
                    const en = typeof m === "string" ? m : m.en;
                    const tr = typeof m === "string" ? "" : m.tr;
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => handleSend(en)}
                        disabled={sending}
                        className="text-[11px] px-2 py-1 rounded-full bg-white border border-slate-300 text-slate-700 hover:bg-blue-50 hover:border-blue-300 transition-colors disabled:opacity-50 text-left"
                        data-testid={`quick-preset-${idx}`}
                        title={tr ? `${en}\n${tr}` : en}
                      >
                        <span className="block">{en.length > 32 ? en.slice(0, 30) + "…" : en}</span>
                        {tr && (
                          <span className="block text-[10px] text-slate-500 italic">
                            {tr.length > 32 ? tr.slice(0, 30) + "…" : tr}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
